import {
  createApiFactory,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
  configApiRef,
  ApiBlueprint,
  type OAuthApi,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type BackstageIdentityApi,
  type SessionApi,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import LockIcon from '@material-ui/icons/Lock';

// Auth API for the "oidc" provider configured in app-config (backed by
// Keycloak - see @backstage/plugin-auth-backend-module-oidc-provider on the
// backend). There's no built-in *AuthApiRef for a generic OIDC provider like
// there is for github/google/etc, so this follows Backstage's documented
// recipe for a custom sign-in provider: a plain OAuth2 client pointed at the
// backend's "oidc" provider id.
export const oidcAuthApiRef = createApiRef<
  OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({ id: 'auth.oidc' });

const oidcApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: oidcAuthApiRef,
        deps: { discoveryApi: discoveryApiRef, oauthRequestApi: oauthRequestApiRef, configApi: configApiRef },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            configApi,
            discoveryApi,
            oauthRequestApi,
            // OAuth2.create defaults this to "development" when omitted,
            // which makes the frontend send env=development on every
            // /oidc/start request - a 404 against app-config.production.yaml's
            // auth.providers.oidc.production block, since only "production"
            // is configured there. This must match that key.
            environment: 'production',
            provider: {
              id: 'oidc',
              title: 'Keycloak',
              message: 'Sign in with your TrueFullstaq account',
              icon: LockIcon,
            },
          }),
      }),
    ),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () =>
      (props: import('@backstage/plugin-app-react').SignInPageProps) => (
        <SignInPage
          {...props}
          align="center"
          providers={[
            {
              id: 'oidc',
              title: 'Keycloak',
              message: 'Sign in with your TrueFullstaq account',
              apiRef: oidcAuthApiRef,
            },
          ]}
        />
      ),
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcApi, signInPage],
});
